/**
 * Scorecard weights and thresholds editor (F9).
 *
 * Two forms: the band cut-offs (A/B on the 0–5 scale) and the per-stage stall
 * thresholds in days. The scorecard criteria and their weights are shown
 * read-only — editing weights safely needs the sum-to-one guard the API
 * enforces, and a half-built weight editor that could save an invalid set is
 * worse than a clear display of the current one. Weights are edited through the
 * API directly until that editor lands; the display makes the current
 * configuration visible, which is what the panel is for.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.13, PRD §5.2, §5.3, F9
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminSettings } from '@nexus/contracts';

import { STAGES, type Stage } from '../../../components/domain/stages';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Field } from '../../../components/ui/Field';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { formatPercent } from '../../../lib/format';
import {
  useAdminSettings,
  useSaveBandThresholds,
  useSaveStallThresholds,
} from '../api/admin.queries';

export function SettingsManager(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAdminSettings();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  if (isPending) {
    return (
      <p className="text-body" aria-busy="true">
        {t('state.loading')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-s4">
      <ScorecardCriteriaCard criteria={data.criteria} />
      <BandsCard settings={data} />
      <StallThresholdsCard settings={data} />
    </div>
  );
}

function ScorecardCriteriaCard({
  criteria,
}: {
  criteria: AdminSettings['criteria'];
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('admin.criteria.title')}</Kicker>
      </CardHead>
      <CardBody>
        <ul className="flex flex-col divide-y divide-gray-100">
          {criteria.map((criterion) => (
            <li key={criterion.key} className="flex items-center justify-between py-2">
              <span className="text-navy-text text-sm">{criterion.label}</span>
              <span className="font-display text-body text-sm tabular-nums">
                {formatPercent(criterion.weight * 100)}
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function BandsCard({ settings }: { settings: AdminSettings }): React.JSX.Element {
  const { t } = useTranslation();
  const save = useSaveBandThresholds();

  const [a, setA] = useState(String(settings.bands.a));
  const [b, setB] = useState(String(settings.bands.b));

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    save.mutate({ a: Number(a), b: Number(b) });
  };

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('admin.bands.title')}</Kicker>
      </CardHead>
      <CardBody>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
          <Field label={t('admin.bands.a')}>
            {({ id, ...described }) => (
              <Input
                id={id}
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={a}
                {...described}
                onChange={(event) => {
                  setA(event.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('admin.bands.b')}>
            {({ id, ...described }) => (
              <Input
                id={id}
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={b}
                {...described}
                onChange={(event) => {
                  setB(event.target.value);
                }}
              />
            )}
          </Field>
          <Button type="submit" disabled={save.isPending}>
            {t('actions.save')}
          </Button>
          {save.isError && (
            <span role="alert" className="text-sem-bad w-full text-xs">
              {t('admin.bands.error')}
            </span>
          )}
        </form>
      </CardBody>
    </Card>
  );
}

function StallThresholdsCard({
  settings,
}: {
  settings: AdminSettings;
}): React.JSX.Element {
  const { t } = useTranslation();
  const save = useSaveStallThresholds();

  /* Seeded from the settings, one string per stage. A blank field means "never
     stalls" (null); a number is the days-to-stall. */
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      STAGES.map((stage) => {
        const current = settings.stallThresholds[stage];
        return [stage, current == null ? '' : String(current)];
      }),
    ),
  );

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const thresholds: Record<string, number | null> = {};
    /* Iterate the state's own entries rather than re-indexing by STAGES: every
       key was seeded from STAGES, so an index lookup would carry an
       unreachable `undefined` fallback. A blank field is "never stalls"
       (null); anything else is the days-to-stall. */
    for (const [stage, raw] of Object.entries(values)) {
      const trimmed = raw.trim();
      thresholds[stage] = trimmed === '' ? null : Number(trimmed);
    }
    save.mutate({ thresholds });
  };

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">{t('admin.stall.title')}</Kicker>
      </CardHead>
      <CardBody>
        <p className="text-body mb-s3 text-xs font-light">{t('admin.stall.hint')}</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          {Object.entries(values).map(([stage, value]) => (
            <StallRow
              key={stage}
              stage={stage as Stage}
              value={value}
              onChange={(next) => {
                setValues((prev) => ({ ...prev, [stage]: next }));
              }}
            />
          ))}
          <div className="mt-s2">
            <Button type="submit" disabled={save.isPending}>
              {t('actions.save')}
            </Button>
          </div>
          {save.isError && (
            <span role="alert" className="text-sem-bad text-xs">
              {t('errors.generic')}
            </span>
          )}
        </form>
      </CardBody>
    </Card>
  );
}

function StallRow({
  stage,
  value,
  onChange,
}: {
  stage: Stage;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Field label={t(`stage.${stage}`)}>
      {({ id, ...described }) => (
        <Input
          id={id}
          type="number"
          min="1"
          max="3650"
          value={value}
          placeholder={t('admin.stall.never')}
          {...described}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
    </Field>
  );
}
